/**
 * sale-hero-v2 slider: autoplay, arrows/dots, swipe, and deferred media hydration.
 */
(function () {
  'use strict';

  function initSaleHero(section) {
    if (!section || section.dataset.sliderReady === 'true') return;

    var viewport = section.querySelector('[data-slider-viewport]');
    var track = section.querySelector('[data-slider-track]');
    if (!track || !viewport) return;

    section.dataset.sliderReady = 'true';

    var slides = Array.prototype.slice.call(section.querySelectorAll('[data-slider-slide]'));
    var prevButton = section.querySelector('[data-slider-prev]');
    var nextButton = section.querySelector('[data-slider-next]');
    var dots = section.querySelectorAll('[data-slider-dot]');
    var total = slides.length;
    var currentIndex = 0;
    var touchStartX = 0;
    var touchStartY = 0;
    var touchActive = false;
    var autoTimer = null;
    var autoplayRemaining = 0;
    var autoplayStartedAt = 0;
    var blockSlideClickUntil = 0;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var transitionStyle = section.dataset.transitionStyle || 'slide';
    var autoplayEnabled = section.dataset.autoplay === 'true' && !reduceMotion;
    var autoplayMs = Math.max(1000, parseInt(section.dataset.autoplaySpeed || '3', 10) * 1000);
    var pauseOnHover = section.dataset.pauseOnHover === 'true';

    function getSlideLink(slide) {
      if (!slide) return null;

      return (
        slide.querySelector('.sale-hero-v2__button-wrap a[href]') ||
        slide.querySelector('a.btn[href]') ||
        slide.querySelector('a[href]')
      );
    }

    function isInteractiveTarget(target) {
      if (!target || !target.closest) return false;

      return Boolean(
        target.closest(
          'a, button, input, select, textarea, label, summary, details, [role="button"], [data-slider-prev], [data-slider-next], [data-slider-dot]'
        )
      );
    }

    slides.forEach(function (slide) {
      var slideLink = getSlideLink(slide);
      if (!slideLink) return;

      slide.classList.add('has-slide-link');

      slide.addEventListener('click', function (event) {
        if (event.defaultPrevented) return;
        if (Date.now() < blockSlideClickUntil) return;
        if (isInteractiveTarget(event.target)) return;

        var currentSlideLink = getSlideLink(slide);
        if (!currentSlideLink) return;

        var destination = currentSlideLink.href;
        if (!destination) return;

        if (event.metaKey || event.ctrlKey || event.shiftKey || currentSlideLink.target === '_blank') {
          window.open(destination, '_blank', 'noopener');
        } else {
          window.location.assign(destination);
        }
      });
    });

    function resetDotProgressElement(fill) {
      if (!fill) return;
      fill.classList.remove('is-running', 'is-complete');
      fill.style.animationDuration = '';
      fill.style.transform = '';
      void fill.offsetWidth;
    }

    function syncDotProgress(options) {
      options = options || {};
      var shouldAnimate = options.animate === true && autoplayEnabled && !reduceMotion;
      var markComplete = options.complete === true || (!autoplayEnabled && !shouldAnimate);

      dots.forEach(function (dot, index) {
        var fill = dot.querySelector('[data-dot-progress]');
        resetDotProgressElement(fill);
        if (!fill || index !== currentIndex) return;

        if (shouldAnimate) {
          fill.style.animationDuration = (options.duration || autoplayMs) + 'ms';
          fill.classList.add('is-running');
        } else if (markComplete) {
          fill.classList.add('is-complete');
        }
      });
    }

    function applyActiveClasses() {
      slides.forEach(function (slide, index) {
        if (index === currentIndex) {
          slide.classList.add('is-active');
          slide.setAttribute('aria-hidden', 'false');
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      dots.forEach(function (dot, index) {
        if (index === currentIndex) {
          dot.classList.add('is-active');
        } else {
          dot.classList.remove('is-active');
        }
      });
    }

    function hydrateSlide(index) {
      var slide = slides[index];
      if (!slide || slide.dataset.mediaHydrated === 'true') return;
      slide.dataset.mediaHydrated = 'true';

      slide.querySelectorAll('picture source[data-srcset]').forEach(function (source) {
        source.setAttribute('srcset', source.getAttribute('data-srcset'));
        source.removeAttribute('data-srcset');
      });

      slide.querySelectorAll('img[data-src]').forEach(function (img) {
        var deferredSrcset = img.getAttribute('data-srcset');
        if (deferredSrcset) {
          img.setAttribute('srcset', deferredSrcset);
          img.removeAttribute('data-srcset');
        }
        img.setAttribute('src', img.getAttribute('data-src'));
        img.removeAttribute('data-src');
      });
    }

    function hydrateAround(index) {
      hydrateSlide(index);
      if (total > 1) hydrateSlide((index + 1) % total);
    }

    function scheduleNeighbourHydration() {
      var run = function () {
        hydrateAround(currentIndex);
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 1200);
      }
    }

    function disableMotionTemporarily() {
      section.classList.add('is-no-motion');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          section.classList.remove('is-no-motion');
        });
      });
    }

    function updateSlider(animate) {
      if (animate === false) {
        disableMotionTemporarily();
      }

      applyActiveClasses();

      if (transitionStyle === 'slide') {
        if (animate === false) {
          track.style.transition = 'none';
        } else {
          track.style.transition = 'transform var(--hero-transition-duration) cubic-bezier(0.22, 1, 0.36, 1)';
        }
        track.style.transform = 'translate3d(-' + currentIndex * 100 + '%, 0, 0)';
      } else {
        track.style.transition = 'none';
        track.style.transform = 'none';
      }

      if (transitionStyle === 'slide' && animate === false) {
        setTimeout(function () {
          track.style.transition = 'transform var(--hero-transition-duration) cubic-bezier(0.22, 1, 0.36, 1)';
        }, 20);
      }
    }

    function clearAutoplayTimer() {
      if (autoTimer) {
        clearTimeout(autoTimer);
        autoTimer = null;
      }
      autoplayStartedAt = 0;
    }

    function pauseAutoplay() {
      if (!autoplayEnabled || total < 2) return;
      if (autoTimer) {
        clearTimeout(autoTimer);
        autoTimer = null;
      }
      if (autoplayStartedAt) {
        autoplayRemaining = Math.max(0, autoplayRemaining - (Date.now() - autoplayStartedAt));
        autoplayStartedAt = 0;
      }
      section.classList.add('is-autoplay-paused');
    }

    function scheduleAutoplay(reset) {
      clearAutoplayTimer();
      section.classList.remove('is-autoplay-paused');

      if (!autoplayEnabled || total < 2) {
        syncDotProgress({ complete: true });
        return;
      }

      if (reset !== false) {
        autoplayRemaining = autoplayMs;
        syncDotProgress({ animate: true, duration: autoplayMs });
      }

      if (autoplayRemaining <= 0) {
        goToSlide(currentIndex + 1, true, false);
        return;
      }

      autoplayStartedAt = Date.now();
      autoTimer = setTimeout(function () {
        goToSlide(currentIndex + 1, true, false);
      }, autoplayRemaining);
    }

    function goToSlide(index, animate, userInitiated) {
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;
      currentIndex = index;
      hydrateAround(currentIndex);
      updateSlider(animate);
      scheduleAutoplay(true);
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        goToSlide(currentIndex - 1, true, true);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        goToSlide(currentIndex + 1, true, true);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var index = parseInt(dot.getAttribute('data-slider-dot'), 10);
        if (!isNaN(index)) {
          goToSlide(index, true, true);
        }
      });
    });

    viewport.addEventListener(
      'touchstart',
      function (event) {
        if (!event.changedTouches || !event.changedTouches.length) return;
        touchActive = true;
        touchStartX = event.changedTouches[0].clientX;
        touchStartY = event.changedTouches[0].clientY;
        pauseAutoplay();
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      function (event) {
        if (!touchActive || !event.changedTouches || !event.changedTouches.length) return;

        var touchEndX = event.changedTouches[0].clientX;
        var touchEndY = event.changedTouches[0].clientY;
        var deltaX = touchEndX - touchStartX;
        var deltaY = touchEndY - touchStartY;

        touchActive = false;

        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
          blockSlideClickUntil = Date.now() + 500;

          if (deltaX < 0) {
            goToSlide(currentIndex + 1, true, true);
          } else {
            goToSlide(currentIndex - 1, true, true);
          }
        } else {
          scheduleAutoplay(false);
        }
      },
      { passive: true }
    );

    if (pauseOnHover) {
      section.addEventListener('mouseenter', function () {
        pauseAutoplay();
      });

      section.addEventListener('mouseleave', function () {
        scheduleAutoplay(false);
      });

      section.addEventListener('focusin', function () {
        pauseAutoplay();
      });

      section.addEventListener('focusout', function () {
        scheduleAutoplay(false);
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        pauseAutoplay();
      } else {
        scheduleAutoplay(false);
      }
    });

    document.addEventListener('shopify:block:select', function (event) {
      if (!section.contains(event.target)) return;
      var slide = event.target.closest('[data-slider-slide]');
      if (!slide) return;
      var index = parseInt(slide.getAttribute('data-slider-slide'), 10);
      if (!isNaN(index)) {
        goToSlide(index, false, true);
      }
    });

    updateSlider(false);
    scheduleAutoplay(true);

    // Warm the next slide once the page is idle so autoplay never reveals a blank slide.
    if (document.readyState === 'complete') {
      scheduleNeighbourHydration();
    } else {
      window.addEventListener('load', scheduleNeighbourHydration, { once: true });
    }
  }

  function initAll() {
    document.querySelectorAll('.sale-hero-v2').forEach(initSaleHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    if (!root) return;

    if (root.classList && root.classList.contains('sale-hero-v2')) {
      initSaleHero(root);
      return;
    }

    var section = root.querySelector ? root.querySelector('.sale-hero-v2') : null;
    if (section) initSaleHero(section);
  });
})();
